import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Hits, Relation } from "../common/types/Paginated";
import { Nullable } from "../common/types/nullable";

type UseTablePaginationOptions<T> = {
  data: T[];
  cursor: Nullable<string>;
  hits?: Hits;
  pageSize?: number;
  fetchNext: (cursor: string) => any;
  // set true if you need to do something else than just dispatch the action
  preventDispatch?: boolean;
};

export const useTablePagination = <T>({
  data,
  cursor,
  hits,
  pageSize = 50,
  fetchNext,
  preventDispatch = false,
}: UseTablePaginationOptions<T>) => {
  const [visitedCursors, setVisitedCursors] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);

  const dispatch = useDispatch();

  const count = {
    value: hits ? hits.value : 0,
    relation: hits ? hits.relation : Relation.DEFAULT,
  };

  const resetPagination = useCallback(() => {
    setPageIndex(0);
    setVisitedCursors([]);
  }, []);

  const pageData = useMemo(() => {
    const start = pageIndex * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, pageIndex, pageSize]);

  const canGoNext = hits ? pageIndex * pageSize < hits.value : false;
  const canGoBack = pageIndex > 0;

  const handleNextPage = useCallback(() => {
    const nextPageIndex = pageIndex + 1;
    setPageIndex(nextPageIndex);
    if (nextPageIndex * pageSize >= data.length && cursor) {
      if (!visitedCursors.includes(cursor)) {
        setVisitedCursors((prev) => [...prev, cursor]);
        if (!preventDispatch) {
          dispatch(fetchNext(cursor));
        } else {
          fetchNext(cursor);
        }
      }
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageIndex,
    pageSize,
    data.length,
    cursor,
    visitedCursors,
    dispatch,
    fetchNext,
  ]);

  const handlePreviousPage = useCallback(() => {
    setPageIndex((prev) => prev - 1);
  }, []);

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      if (newPage > pageIndex && canGoNext) {
        handleNextPage();
        setPageIndex(newPage);
      } else if (newPage < pageIndex && canGoBack) {
        handlePreviousPage();
        setPageIndex(newPage);
      }
    },
    [pageIndex, canGoNext, canGoBack, handleNextPage, handlePreviousPage],
  );

  return {
    pageData,
    onPageChange,
    canGoNext,
    canGoBack,
    pageIndex,
    count,
    resetPagination,
  };
};
